import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useUnifiedAuth } from '../hooks/useUnifiedAuth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StarRating = ({ rating, onRate, interactive = false, size = 'md' }) => {
  const [hover, setHover] = useState(0);
  const sizeClass = size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-7 h-7' : 'w-5 h-5';

  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={!interactive}
          onClick={() => interactive && onRate?.(star)}
          onMouseEnter={() => interactive && setHover(star)}
          onMouseLeave={() => interactive && setHover(0)}
          className={`${interactive ? 'cursor-pointer hover:scale-110' : 'cursor-default'} transition-transform`}
          data-testid={`star-${star}`}
        >
          <svg
            className={`${sizeClass} ${
              star <= (hover || rating) ? 'text-amber-400' : 'text-gray-300'
            } transition-colors`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        </button>
      ))}
    </div>
  );
};

const ReviewSection = ({ providerId, providerType = 'provider' }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const { user, sessionToken } = useUnifiedAuth();
  const [reviews, setReviews] = useState([]);
  const [averageRating, setAverageRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [reviewableAppointments, setReviewableAppointments] = useState([]);
  const [selectedAppointment, setSelectedAppointment] = useState('');
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const endpoint = providerType === 'doctor' ? 'doctors' : 'providers';

  useEffect(() => {
    fetchReviews();
    if (user && sessionToken) {
      fetchReviewableAppointments();
    }
  }, [providerId, user]);

  const fetchReviews = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/${endpoint}/${providerId}/reviews`);
      const data = await res.json();
      setReviews(data.reviews || []);
      setAverageRating(data.average_rating || 0);
      setTotalReviews(data.total || 0);
    } catch (err) {
      console.error('Error fetching reviews:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchReviewableAppointments = async () => {
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/user/reviewable-appointments?provider_id=${providerId}&provider_type=${providerType}`,
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      const data = await res.json();
      setReviewableAppointments(data.appointments || []);
    } catch (err) {
      console.error('Error fetching reviewable appointments:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!selectedAppointment) {
      setError('Please select an appointment to review');
      return;
    }
    if (rating === 0) {
      setError('Please select a rating');
      return;
    }
    if (reviewText && reviewText.split(/\s+/).filter(Boolean).length > 400) {
      setError('Review must be 400 words or less');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/${endpoint}/${providerId}/reviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionToken}`,
        },
        body: JSON.stringify({
          appointment_id: selectedAppointment,
          rating,
          review_text: reviewText,
        }),
      });

      if (res.ok) {
        setSuccess('Review submitted successfully!');
        setRating(0);
        setReviewText('');
        setSelectedAppointment('');
        setShowForm(false);
        fetchReviews();
        fetchReviewableAppointments();
      } else {
        const data = await res.json();
        setError(data.detail || 'Failed to submit review');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const wordCount = reviewText.split(/\s+/).filter(Boolean).length;

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  const ratingDistribution = [5, 4, 3, 2, 1].map((star) => ({
    star,
    count: reviews.filter((r) => r.rating === star).length,
    pct: totalReviews > 0 ? (reviews.filter((r) => r.rating === star).length / totalReviews) * 100 : 0,
  }));

  return (
    <div className="mt-8" data-testid="review-section" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Rating Summary */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">{t('reviews.title')}</h3>
        <div className="flex items-start gap-8">
          <div className="text-center">
            <div className="text-4xl font-bold text-gray-900">{averageRating || '—'}</div>
            <StarRating rating={Math.round(averageRating)} size="sm" />
            <p className="text-sm text-gray-500 mt-1">{totalReviews} {totalReviews !== 1 ? t('reviews.reviews') : t('reviews.review')}</p>
          </div>
          <div className="flex-1 space-y-1">
            {ratingDistribution.map(({ star, count, pct }) => (
              <div key={star} className="flex items-center gap-2 text-sm">
                <span className="w-3 text-gray-600">{star}</span>
                <svg className="w-3.5 h-3.5 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-amber-400 rounded-full" style={{ width: `${pct}%` }} />
                </div>
                <span className="w-6 text-right text-gray-500">{count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Write Review Button */}
        {user && reviewableAppointments.length > 0 && !showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="mt-4 w-full py-2.5 border-2 border-blue-600 text-blue-600 rounded-lg font-medium hover:bg-blue-50 transition-colors"
            data-testid="write-review-btn"
          >
            {t('reviews.writeReview')}
          </button>
        )}
      </div>

      {/* Review Form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6" data-testid="review-form">
          <h4 className="font-semibold text-gray-900 mb-4">{t('reviews.yourReview')}</h4>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('reviews.selectAppointment')}</label>
              <select
                value={selectedAppointment}
                onChange={(e) => setSelectedAppointment(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="appointment-select"
              >
                <option value="">{t('reviews.chooseAppointment')}</option>
                {reviewableAppointments.map((apt) => (
                  <option key={apt.id} value={apt.id}>
                    {apt.date || apt.appointment_date} — {apt.service_name || apt.reason || 'Appointment'}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('reviews.yourRating')}</label>
              <StarRating rating={rating} onRate={setRating} interactive size="lg" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('reviews.reviewText')} <span className="text-gray-400">({t('reviews.reviewOptional')})</span>
              </label>
              <textarea
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                rows={4}
                maxLength={3000}
                placeholder={t('reviews.shareExperience')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                data-testid="review-text"
              />
              <p className={`text-xs mt-1 ${wordCount > 400 ? 'text-red-500' : 'text-gray-400'}`}>
                {wordCount}/400 {t('reviews.words')}
              </p>
            </div>

            <p className="text-xs text-gray-500">{t('reviews.anonymousNote')}</p>

            {error && <p className="text-sm text-red-600" data-testid="review-error">{error}</p>}
            {success && <p className="text-sm text-green-600" data-testid="review-success">{success}</p>}

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 bg-blue-600 text-white py-2.5 rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
                data-testid="submit-review-btn"
              >
                {submitting ? t('reviews.submitting') : t('reviews.submitReview')}
              </button>
              <button
                type="button"
                onClick={() => { setShowForm(false); setError(''); }}
                className="px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Reviews List */}
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
        </div>
      ) : reviews.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <p className="text-gray-500">{t('reviews.noReviews')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reviews.map((review) => (
            <div key={review.id} className="bg-white rounded-xl border border-gray-200 p-5" data-testid="review-card">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                    <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{t('reviews.verifiedClient')}</p>
                    <p className="text-xs text-gray-500">{formatDate(review.created_at)}</p>
                  </div>
                </div>
                <StarRating rating={review.rating} size="sm" />
              </div>
              {review.review_text && (
                <p className="text-gray-700 text-sm leading-relaxed mt-2">{review.review_text}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewSection;
